import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  onSnapshot,
} from 'firebase/firestore'
import { db } from '../config/firebase'

export type ProjectCategory =
  | 'Oficinas corporativas'
  | 'Sucursales bancarias'
  | 'Retail y tiendas'
  | 'Educación / institucional'
  | 'Industrial / especial'
  | 'Otros'

export interface ProjectData {
  id?: string
  name: string
  category: ProjectCategory
  images: string[]
  description: string
  year: number
  m2: number
  comuna: string
  featured: boolean
}

const PROJECTS_COLLECTION = 'projects'
const CACHE_KEY = 'forax_projects_cache'

// Proyectos iniciales (solo se usan si Firebase está vacío)
const INITIAL_PROJECTS: Omit<ProjectData, 'id'>[] = [
  {
    name: 'Banco Itaú',
    category: 'Sucursales bancarias',
    images: [
      'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&q=80&w=1000',
    ],
    description: 'Habilitación Sucursal Viña del Mar 3',
    year: 2004,
    m2: 454,
    comuna: 'Viña del Mar',
    featured: false,
  },
  {
    name: 'Scotiabank',
    category: 'Sucursales bancarias',
    images: [
      'https://images.unsplash.com/photo-1601597111158-2fceff292cdc?auto=format&fit=crop&q=80&w=1000',
    ],
    description: 'Escala Auxiliar Exterior Alameda 969',
    year: 2013,
    m2: 902,
    comuna: 'Santiago',
    featured: false,
  },
  {
    name: 'Correa Gubbins Abogados',
    category: 'Oficinas corporativas',
    images: [
      'https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80&w=1000',
    ],
    description: 'Habilitación y Remodelación Pisos 9 y 11, Apoquindo 3500',
    year: 2015,
    m2: 1808,
    comuna: 'Las Condes',
    featured: true,
  },
]

export function useProjects() {
  // Cargar desde caché inmediatamente
  const getCachedProjects = (): ProjectData[] => {
    try {
      const cached = localStorage.getItem(CACHE_KEY)
      return cached ? JSON.parse(cached) : []
    } catch {
      return []
    }
  }

  const [projects, setProjects] = useState<ProjectData[]>(getCachedProjects())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadProjects = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const projectsRef = collection(db, PROJECTS_COLLECTION)
      const q = query(projectsRef, orderBy('year', 'desc'))
      const querySnapshot = await getDocs(q)

      const loadedProjects: ProjectData[] = []
      querySnapshot.forEach((doc) => {
        loadedProjects.push({ id: doc.id, ...doc.data() } as ProjectData)
      })

      // Si no hay proyectos, inicializar con datos por defecto
      if (loadedProjects.length === 0) {
        console.log('Inicializando proyectos por defecto...')
        for (const project of INITIAL_PROJECTS) {
          await addDoc(collection(db, PROJECTS_COLLECTION), project)
        }
        // Recargar después de inicializar
        const newSnapshot = await getDocs(q)
        const newProjects: ProjectData[] = []
        newSnapshot.forEach((doc) => {
          newProjects.push({ id: doc.id, ...doc.data() } as ProjectData)
        })
        localStorage.setItem(CACHE_KEY, JSON.stringify(newProjects))
        setProjects(newProjects)
      } else {
        localStorage.setItem(CACHE_KEY, JSON.stringify(loadedProjects))
        setProjects(loadedProjects)
      }

      setError(null)
    } catch (err: any) {
      console.error('Error loading projects:', err)

      let errorMessage = 'Error al cargar proyectos.'

      if (err.message?.includes('Timeout')) {
        errorMessage =
          'Firebase no responde. ¿Activaste Firestore Database en Firebase Console?'
      } else if (err.code === 'permission-denied') {
        errorMessage =
          'Permisos denegados. Verifica las reglas de seguridad en Firestore.'
      } else if (err.code === 'unavailable') {
        errorMessage =
          'Firebase no disponible. Verifica tu conexión a internet.'
      } else {
        errorMessage = `Error: ${err.message || 'Problema de conexión con Firebase'}`
      }

      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [])

  // Cargar proyectos desde Firebase al montar
  useEffect(() => {
    loadProjects()
  }, [loadProjects])

  const addProject = useCallback(
    async (project: ProjectData) => {
      try {
        const docRef = await addDoc(
          collection(db, PROJECTS_COLLECTION),
          project,
        )
        const newProject = { ...project, id: docRef.id }
        const updatedProjects = [newProject, ...projects]
        setProjects(updatedProjects)
        localStorage.setItem(CACHE_KEY, JSON.stringify(updatedProjects))
      } catch (err) {
        console.error('Error adding project:', err)
        alert('Error al crear proyecto. Verifica tu conexión.')
      }
    },
    [projects],
  )

  const updateProject = useCallback(
    async (id: string, updatedProject: ProjectData) => {
      try {
        const projectRef = doc(db, PROJECTS_COLLECTION, id)
        await updateDoc(projectRef, updatedProject as any)
        const updatedProjects = projects.map((p) =>
          p.id === id ? { ...updatedProject, id } : p,
        )
        setProjects(updatedProjects)
        localStorage.setItem(CACHE_KEY, JSON.stringify(updatedProjects))
      } catch (err) {
        console.error('Error updating project:', err)
        alert('Error al actualizar proyecto. Verifica tu conexión.')
      }
    },
    [projects],
  )

  const deleteProject = useCallback(
    async (id: string) => {
      try {
        await deleteDoc(doc(db, PROJECTS_COLLECTION, id))
        const updatedProjects = projects.filter((p) => p.id !== id)
        setProjects(updatedProjects)
        localStorage.setItem(CACHE_KEY, JSON.stringify(updatedProjects))
      } catch (err) {
        console.error('Error deleting project:', err)
        alert('Error al eliminar proyecto. Verifica tu conexión.')
      }
    },
    [projects],
  )

  const toggleFeatured = useCallback(
    async (id: string): Promise<boolean> => {
      const project = projects.find((p) => p.id === id)
      if (!project) return false

      // Si ya está destacado, lo quitamos
      if (project.featured) {
        await updateProject(id, { ...project, featured: false })
        return true
      }

      // Si no está destacado, verificamos si hay cupo (máximo 3)
      const featuredCount = projects.filter((p) => p.featured).length
      if (featuredCount >= 3) {
        return false
      }

      await updateProject(id, { ...project, featured: true })
      return true
    },
    [projects, updateProject],
  )

  return {
    projects,
    loading,
    error,
    addProject,
    updateProject,
    deleteProject,
    toggleFeatured,
    reloadProjects: loadProjects,
  }
}
